// Presentation only: unknown applicability is not a participant request or an exemption.
// Explicit requests must come from the authorized case-management integration.
export function documentVisibility(
  document: any,
  applies: boolean | "unknown",
  request?: { status?: string; instructions?: { en?: string; es?: string } },
  hasFile = false,
  hasResponse = false,
): "requested" | "retained" | "not_applicable" | "unresolved" {
  if (request?.status === "requested" && request.instructions?.en?.trim()) return "requested";
  if (applies === true) return "requested";
  if (hasFile || hasResponse) return "retained";
  return applies === false ? "not_applicable" : "unresolved";
}

export function documentItems(
  documents: any[],
  applicabilityFor: (document: any) => boolean | "unknown",
  requests: Record<string, any> = {},
  files: Record<string, string> = {},
  responses: Record<string, string> = {},
) {
  return documents.map((document) => ({
    document,
    request: requests[document.id],
    state: documentVisibility(
      document,
      applicabilityFor(document),
      requests[document.id],
      Boolean(files[document.id]),
      Boolean(responses[document.id]),
    ),
  }));
}

const EVIDENCE = "pdoc.us.evidence_record_contributions";
const FAMILY = "pdoc.us.family_documents";
const text = (value: unknown) => (typeof value === "string" ? value.trim() : "");
const records = (value: unknown): Record<string, any>[] =>
  Array.isArray(value) ? value.filter((v): v is Record<string, any> => Boolean(v && typeof v === "object" && !Array.isArray(v))) : [];

// Content-based identity avoids associating a file with another person/item after reordering.
// Production repeaters should supply persistent record IDs through the case adapter.
function child(parent: any, kind: string, identity: unknown[], title: { en: string; es: string }, description: { en: string; es: string }) {
  return {
    ...parent,
    id: `${parent.id}::${kind}::${encodeURIComponent(JSON.stringify(identity))}`,
    parentId: parent.id,
    title,
    description,
    example: null,
  };
}

function evidenceRows(parent: any, answers: Record<string, unknown>) {
  const rows: any[] = [];
  for (const entry of records(answers["pf.us.awards"])) {
    const name = text(entry.award_name);
    if (!name) continue;
    const context = [name, text(entry.category), text(entry.year)].filter(Boolean).join(", ");
    const won = entry.result === "won";
    rows.push(
      child(
        parent,
        "award",
        [name, entry.category, entry.year, entry.result, entry.work, entry.awarding_body],
        {
          en: `${won ? "Award certificate" : "Award or nomination record"}: ${name}`,
          es: `${won ? "Certificado del premio" : "Documento del premio o nominación"}: ${name}`,
        },
        {
          en: `Upload the certificate or official record for ${context}${text(entry.work) ? ` for “${text(entry.work)}”` : ""}.`,
          es: `Suba el certificado o documento oficial de ${context}${text(entry.work) ? ` por “${text(entry.work)}”` : ""}.`,
        },
      ),
    );
  }
  for (const entry of records(answers["pf.us.press"])) {
    const title = text(entry.title);
    if (!title) continue;
    rows.push(
      child(
        parent,
        "press",
        [title, entry.publication, entry.publication_date, entry.url],
        { en: `Published article: ${title}`, es: `Artículo publicado: ${title}` },
        {
          en: `Upload a copy of “${title}”${text(entry.publication) ? ` from ${text(entry.publication)}` : ""}, showing the publication and date.`,
          es: `Suba una copia de “${title}”${text(entry.publication) ? ` de ${text(entry.publication)}` : ""}, con la publicación y la fecha visibles.`,
        },
      ),
    );
  }
  for (const entry of records(answers["pf.us.commercial_success"])) {
    const work = text(entry.work);
    if (!work) continue;
    rows.push(
      child(
        parent,
        "results",
        [work, entry.measure_type, entry.result, entry.source],
        { en: `Published results: ${work}`, es: `Resultados publicados: ${work}` },
        {
          en: `Upload the report or published record supporting the results you listed for “${work}”${text(entry.source) ? ` from ${text(entry.source)}` : ""}.`,
          es: `Suba el informe o documento publicado que respalda los resultados que indicó para “${work}”${text(entry.source) ? ` de ${text(entry.source)}` : ""}.`,
        },
      ),
    );
  }
  for (const entry of records(answers["pf.us.compensation_records"])) {
    const work = text(entry.production_or_employer);
    if (!work) continue;
    rows.push(
      child(
        parent,
        "pay",
        [work, entry.role, entry.amount, entry.currency, entry.basis, entry.evidence_type],
        { en: `Pay record: ${work}`, es: `Documento de remuneración: ${work}` },
        {
          en: `Upload the contract or pay record supporting the amount you listed for ${work}.`,
          es: `Suba el contrato o documento de remuneración que respalda el importe que indicó para ${work}.`,
        },
      ),
    );
  }
  return [...new Map(rows.map((row) => [row.id, row])).values()];
}

function familyRows(parent: any, answers: Record<string, unknown>) {
  const rows = records(answers["pf.family.members"]).flatMap((entry) => {
    const name = [entry.given_names, entry.middle_names, entry.surnames, entry.family_primary, entry.family_second, entry.full_name]
      .map(text)
      .filter(Boolean)
      .join(" ");
    if (!name) return [];
    const identity = [name, entry.birth_date, entry.relationship];
    return [
      child(
        parent,
        "family-passport",
        identity,
        { en: `Passport identity page: ${name}`, es: `Página de datos del pasaporte: ${name}` },
        {
          en: `Upload the passport page showing ${name}’s photo and personal details.`,
          es: `Suba la página del pasaporte con la foto y los datos personales de ${name}.`,
        },
      ),
      child(
        parent,
        "family-relationship",
        identity,
        { en: `Relationship document: ${name}`, es: `Documento de parentesco: ${name}` },
        {
          en: `Upload the document showing your relationship to ${name}, such as a marriage certificate for a spouse or a birth certificate for a child.`,
          es: `Suba el documento que acredita su parentesco con ${name}, como el certificado de matrimonio para su cónyuge o el de nacimiento para un hijo.`,
        },
      ),
    ];
  });
  return [...new Map(rows.map((row) => [row.id, row])).values()];
}

// Expand only canonical categories with authored structured records. Do not parse biographies
// or invent a universal visa checklist. Original IDs stay as parents for case integration.
export function personalizeDocuments(
  documents: any[],
  answers: Record<string, unknown>,
  requests: Record<string, any> = {},
  saved: Record<string, any> = {},
  files: Record<string, string> = {},
  responses: Record<string, string> = {},
) {
  const result: any[] = [];
  const pending: { document: any; task: "professional_profile" | "accompanying_family" }[] = [];
  for (const document of documents) {
    if ((document.id !== EVIDENCE && document.id !== FAMILY) || (requests[document.id]?.status === "requested" && requests[document.id]?.instructions?.en?.trim())) {
      result.push(document);
      continue;
    }
    const children = document.id === EVIDENCE ? evidenceRows(document, answers) : familyRows(document, answers);
    result.push(...children);
    if (!children.length) pending.push({ document, task: document.id === EVIDENCE ? "professional_profile" : "accompanying_family" });
    // Keep an older category-level selection without treating it as all child records.
    if (files[document.id] || responses[document.id]) result.push({ ...document, retained: true });
  }
  const currentIds = new Set(result.map((d) => d.id));
  for (const document of Object.values(saved)) {
    if (document.parentId && documents.some((d) => d.id === document.parentId) && !currentIds.has(document.id) && (files[document.id] || responses[document.id])) {
      result.push({ ...document, retained: true });
    }
  }
  return { documents: result, pending };
}

// Passport has its own section. Professional evidence will be collected by the
// future post-profile analysis flow; retain the canonical requirements and data.
export function documentCenterRequests(documents: any[]) {
  const deferred = new Set(["pdoc.us.evidence_record_contributions", "pdoc.us.o2_support", "pdoc.it.qualification_certificate"]);
  return documents.filter((document) => !document.id.endsWith(".passport") && !deferred.has(document.id));
}
